import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
    Home,
    School,
} from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useUser } from "@/contexts/UserContext";
import { startOfYear, endOfYear } from "date-fns";
import { DateRange } from "react-day-picker";
import { BoletoTransaction } from "@/lib/api";
import { Boleto } from "@/pages/Backoffice/Clients/Boleto";
import { FinancialTableView } from "@/components/Financial/FinancialTableView";
import { useFinancialData } from "@/hooks/useFinancialData";
import { useBoletoViewer } from "@/hooks/useBoletoViewer";
import { buildBoletoParcelasFromTransaction } from "@/utils/financialUtils";

const Financial = () => {
    const { ConfirmDialog } = useConfirmDialog();
    const { selectedClient } = useUser();

    const hoje = useMemo(() => new Date(), []);
    const [date, setDate] = useState<DateRange | undefined>({
        from: startOfYear(hoje),
        to: endOfYear(hoje)
    });

    const { data, isLoading, refetch } = useFinancialData(
        selectedClient?.cnpj as string | undefined,
        date,
    );
    const { boleto, currentGeneratingBoletoId, unavailableBoletoIds, openBoleto, closeBoleto } = useBoletoViewer({
        onUnavailable: () => refetch(),
    });

    const transactions = data?.customerTransactionsList || [];
    const summary = data?.summary || { total: 0, liquidado: 0, vencidos: 0, emAberto: 0 };

    const handleGenerateBoleto = (transaction: BoletoTransaction) =>
        openBoleto(buildBoletoParcelasFromTransaction(transaction));

    const renderContent = () => {
        if (!selectedClient) {
            return (
                <div className="py-12 text-center">
                    <School className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h3 className="mt-4 text-lg font-medium">
                        Selecione uma unidade escolar para ver os dados financeiros.
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Use o seletor no topo da página para escolher uma unidade.
                    </p>
                </div>
            );
        }

        return (
            <FinancialTableView
                transactions={transactions}
                summary={summary}
                isLoading={isLoading}
                onGenerateBoleto={handleGenerateBoleto}
                date={date}
                setDate={setDate}
                currentGeneratingBoletoId={currentGeneratingBoletoId}
                unavailableBoletoIds={unavailableBoletoIds}
            />
        );
    }

    return (
        <>
            <ConfirmDialog />
            <div className="min-h-screen bg-muted/50">
                <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-30 shadow-sm">
                    <div className="container mx-auto px-4 md:px-6 py-4">
                        <Breadcrumb className="mb-4">
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink asChild>
                                        <Link to="/"><Home className="h-4 w-4" /></Link>
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="font-bold text-primary">Módulo Financeiro</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>

                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                            <div className="space-y-1">
                                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Gestão Financeira</h1>
                            </div>

                            {selectedClient && (
                                <div className="flex items-center gap-4 bg-white p-3 px-5 rounded-2xl border border-border shadow-sm transition-all hover:shadow-md">
                                    <div className="h-11 w-11 rounded-xl bg-foreground flex items-center justify-center text-white font-bold shadow-lg">
                                        {selectedClient.nome_cliente.substring(0, 1).toUpperCase()}
                                    </div>
                                    <div className="flex flex-col border-r border-border pr-5">
                                        <span className="text-[11px] font-bold text-foreground leading-none uppercase tracking-tight">{selectedClient.nome_cliente}</span>
                                        <span className="text-[10px] text-muted-foreground font-bold mt-2 tracking-widest">{selectedClient.codigo_cnx}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest leading-none mb-1">CNPJ</span>
                                        <span className="text-xs font-mono font-bold text-muted-foreground">{selectedClient.cnpj}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className="container mx-auto px-4 md:px-6 py-8 space-y-8">
                    {renderContent()}
                </main>

                {!!selectedClient && boleto && date && (
                    <Boleto
                        installment={boleto}
                        cnpj={selectedClient?.cnpj}
                        date={date}
                        setInstallment={closeBoleto}
                    />
                )}
            </div>
        </>
    );
};

export default Financial;
